import { PlatformCompany } from '../../../types/Company'
import { Address } from '../../../types/API'
import { EntityTransformer, ValidationResult } from './types'
import { SectorOptions, OwnershipStructureOptions, CountryOptions } from '../../../constants/statsOptions'
import { SectorAliases, OwnershipStructureAliases, CountryAliases } from '../index'
import normalizeEmployeeCount from '../../util/normalizeEmployeeCount'
import normalizeMarketCap from '../../util/normalizeMarketCap'
import fuzzyNormalize from '../../util/fuzzyNormalize'

export interface CompanyFormData {
  name?: string
  registrationNumber?: string
  phoneNumber?: string
  employeeCount?: string
  sector?: string
  marketCap?: string
  ownershipStructure?: string
  address?: {
    line1?: string
    line2?: string
    city?: string
    region?: string
    postalCode?: string
    country?: string
  }
}

export const companyTransformer: EntityTransformer<PlatformCompany, CompanyFormData> = {
  entityType: 'Company',
  toEntity (raw: Record<string, unknown>): Partial<PlatformCompany> {
    const rawAddress = raw.address as Record<string, unknown> | undefined

    const hasAddressFields =
      rawAddress?.line1 ||
      rawAddress?.line2 ||
      rawAddress?.city ||
      rawAddress?.region ||
      rawAddress?.postalCode ||
      rawAddress?.country

    const address: Address | undefined = hasAddressFields
      ? {
          line1: rawAddress?.line1 as string | undefined,
          line2: rawAddress?.line2 as string | undefined,
          city: rawAddress?.city as string | undefined,
          region: rawAddress?.region as string | undefined,
          postalCode: rawAddress?.postalCode as string | undefined,
          country: fuzzyNormalize(rawAddress?.country as string | undefined, CountryOptions, CountryAliases)
        } as Address
      : undefined

    return {
      name: raw.name as string | undefined,
      registrationNumber: raw.registrationNumber != null ? String(raw.registrationNumber) : undefined,
      phoneNumber: raw.phoneNumber != null ? String(raw.phoneNumber) : undefined,
      employeeCount: normalizeEmployeeCount(raw.employeeCount as string | number | undefined),
      sector: fuzzyNormalize(raw.sector as string | undefined, SectorOptions, SectorAliases),
      marketCap: normalizeMarketCap(raw.marketCap as string | number | undefined),
      ownershipStructure: fuzzyNormalize(raw.ownershipStructure as string | undefined, OwnershipStructureOptions, OwnershipStructureAliases),
      ...(address && { address })
    }
  },
  toFormData (entity: Partial<PlatformCompany>): CompanyFormData {
    return {
      name: entity.name ?? undefined,
      registrationNumber: entity.registrationNumber ?? undefined,
      phoneNumber: entity.phoneNumber ?? undefined,
      employeeCount: entity.employeeCount ?? undefined,
      sector: entity.sector ?? undefined,
      marketCap: entity.marketCap ?? undefined,
      ownershipStructure: entity.ownershipStructure ?? undefined,
      address: {
        line1: entity.address?.line1 ?? undefined,
        line2: entity.address?.line2 ?? undefined,
        city: entity.address?.city ?? undefined,
        region: entity.address?.region ?? undefined,
        postalCode: entity.address?.postalCode ?? undefined,
        country: entity.address?.country ?? undefined
      }
    }
  },
  fromFormData (formData: CompanyFormData): Record<string, unknown> {
    const result: Record<string, unknown> = {
      name: formData.name,
      registrationNumber: formData.registrationNumber,
      phoneNumber: formData.phoneNumber,
      employeeCount: formData.employeeCount,
      sector: formData.sector,
      marketCap: formData.marketCap,
      ownershipStructure: formData.ownershipStructure
    }

    if (formData.address) {
      if (formData.address.line1) {
        result['address.line1'] = formData.address.line1
      }
      if (formData.address.line2) {
        result['address.line2'] = formData.address.line2
      }
      if (formData.address.city) {
        result['address.city'] = formData.address.city
      }
      if (formData.address.region) {
        result['address.region'] = formData.address.region
      }
      if (formData.address.postalCode) {
        result['address.postalCode'] = formData.address.postalCode
      }
      if (formData.address.country) {
        result['address.country'] = formData.address.country
      }
    }

    return result
  },
  validate (entity: Partial<PlatformCompany>): ValidationResult {
    const missingFields: string[] = []

    if (!entity.name) {
      missingFields.push('Company Name')
    }
    if (!entity.employeeCount) {
      missingFields.push('Employee Count')
    }
    if (!entity.sector) {
      missingFields.push('Sector')
    }
    if (!entity.marketCap) {
      missingFields.push('Market Cap')
    }
    if (!entity.ownershipStructure) {
      missingFields.push('Ownership Structure')
    }
    if (!entity.address?.country) {
      missingFields.push('Country')
    }

    return {
      valid: missingFields.length === 0,
      missingFields
    }
  }
}
